import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/core/helpers/cn";
import formatPrice from "@/core/helpers/formatPrice";
import { MapPin2Icon, User2Icon, Vehicle2Icon } from "@/shared/icons";
import { Icon } from "@/shared/ui";
import { EstateState } from "@/domains/estate/slice";
import EstateOwnerInfo from "@/domains/estate/components/EstateOwnerInfo.tsx";

type Props = {
  info: EstateState["info"];
  className?: string;
  hideDetails?: boolean;
};
export default function EstateMainInfo({ info, className, hideDetails = false }: Props) {
  const { t } = useTranslation("estate");

  const existTenants = useMemo(() => info.tenants.filter((item) => item !== null), [info.tenants]);
  const nameTenants = useMemo(() => existTenants.filter((item) => item), [existTenants]);
  const existVehicles = useMemo(
    () => info.vehicles.filter((item) => item !== null),
    [info.vehicles],
  );
  const nameVehicles = useMemo(() => existVehicles.filter((item) => item), [existVehicles]);

  return (
    <div className={cn("flex flex-col gap-5", className)}>
      <div className="flex flex-col w-full gap-3.5 px-1">
        <EstateOwnerInfo owner={info.owner} />
        <div className="flex items-center gap-2.5">
          <MapPin2Icon size={20} className="text-white" />
          <div className="text-16 text-white font-medium">{info.address}</div>
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2.5">
            <User2Icon size={20} className="text-white" />
            <div className="text-14 text-white font-bold uppercase">
              {t("estate.info.tenants")} ({existTenants.length}/{info.tenants.length})
            </div>
          </div>
          {info.isResident && !hideDetails && (
            <div className="text-14 text-white/70 font-medium pl-7">{nameTenants.join(", ")}</div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2.5">
            <Vehicle2Icon size={20} className="text-white" />
            <div className="text-14 text-white font-bold uppercase">
              {t("estate.info.vehicles")} ({existVehicles.length}/{info.vehicles.length})
            </div>
          </div>
          {info.isResident && !hideDetails && (
            <div className="text-14 text-white/70 font-medium pl-7">{nameVehicles.join(", ")}</div>
          )}
        </div>
      </div>
      <div className="flex flex-col">
        <div className="flex items-center justify-between py-3.5 px-1 border-t border-t-[#191919]">
          <div className="text-16 font-medium text-white"> {t("estate.cost")}:</div>
          <div className="text-15 font-bold text-white">{formatPrice(info.price)} ₽</div>
        </div>

        <div className="flex items-center justify-between py-3.5 px-1 border-t border-t-[#191919]">
          <div className="text-16 font-medium text-white"> {t("estate.feePerDay")}:</div>
          <div className="text-15 font-bold text-white">{formatPrice(info.totalFee)} ₽</div>
        </div>

        {!info.isSale && (
          <div className="flex items-center justify-between pt-3.5 px-1 border-t border-t-[#191919]">
            <div className="text-16 font-medium text-white">
              {t("estate.tenantsAndServicesFee")}:
            </div>
            <div className="text-15 font-bold text-white">
              {formatPrice(info.totalFee - info.baseFee)} ₽
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
